import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BedDouble, ExternalLink, Hotel, Loader2, MapPin, Search, Star, Users, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HotelVendorBadge } from '@/components/hotels/hotel-vendor-badge';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';
import { StayLine } from '@/components/allocation/stay-line';
import type { HotelBoardBooking, HotelBoardGuest, IHotelBoard, IHotelOccupancy } from './types';
import { Unpriced } from '@/components/allocation/unpriced';

// "Who's where" — the hotel-occupancy board (the MVP rooming view at the new stack's
// truthful granularity). Booking models a reservation per PNR (not physical rooms), so we
// show each booked HOTEL PNR + its guests under its hotel, enriched with geo (stars /
// distance-from-airport / nightly rate). Quick PNR search + per-hotel filter.
//
// PRESENTATIONAL + SCOPE-NEUTRAL: it renders a ready `IHotelBoard` handed in by a
// scope adapter (event scope → the disruption roster; case scope → the case groups).
// No data fetching here — `useHotelBoard` (or any future case-scoped hook) owns that.

export interface HotelOccupancyBoardProps {
  board: IHotelBoard | undefined;
  isLoading?: boolean;
  isError?: boolean;
  /** Pre-select one hotel's filter chip (a deep link `?hotel=`); the chips stay the truth afterwards. */
  initialHotel?: string | undefined;
  /** Additive slot under each hotel card's header (e.g. the shuttle transfer strip). */
  renderHotelExtra?: ((hotel: IHotelOccupancy) => ReactNode) | undefined;
}

export function HotelOccupancyBoard({
  board,
  isLoading,
  isError,
  initialHotel,
  renderHotelExtra,
}: HotelOccupancyBoardProps) {
  const { t } = useTranslation('cases');
  const [query, setQuery] = useState('');
  const [hotelFilter, setHotelFilter] = useState<string | 'all'>(initialHotel ?? 'all');
  useEffect(() => {
    if (initialHotel) setHotelFilter(initialHotel);
  }, [initialHotel]);

  const q = query.trim().toUpperCase();
  const visible = useMemo(() => {
    if (!board) return [];
    return board.hotels
      .filter((h) => hotelFilter === 'all' || h.hotelUrn === hotelFilter)
      .map((h) => ({
        ...h,
        pnrs: q ? h.pnrs.filter((g) => g.pnrLabel.toUpperCase().includes(q)) : h.pnrs,
      }))
      .filter((h) => h.pnrs.length > 0);
  }, [board, hotelFilter, q]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground" data-testid="hotel-board-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t('board.loading')}
      </div>
    );
  }
  if (isError) {
    return (
      <p className="py-6 text-sm text-destructive" data-testid="hotel-board-error">
        {t('board.error')}
      </p>
    );
  }
  if (!board || board.hotels.length === 0) {
    return (
      <div data-testid="hotel-board-empty">
        <EmptyState
          icon={<Hotel className="h-6 w-6" aria-hidden />}
          title={t('board.empty.title')}
          description={t('board.empty.description')}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="hotel-board">
      {/* Toolbar: PNR search + per-hotel filter chips */}
      <div className="flex shrink-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('board.searchPlaceholder')}
              aria-label={t('board.searchLabel')}
              data-testid="hotel-board-search"
              className="h-8 w-44 rounded-md border border-border bg-background pl-8 pr-7 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            {query ? (
              <button
                onClick={() => setQuery('')}
                aria-label={t('board.clearSearch')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterChip active={hotelFilter === 'all'} onClick={() => setHotelFilter('all')}>
              {t('board.allHotels')}
            </FilterChip>
            {board.hotels.map((h) => (
              <FilterChip
                key={h.hotelUrn}
                active={hotelFilter === h.hotelUrn}
                onClick={() => setHotelFilter(h.hotelUrn)}
              >
                {h.airport ? `${h.airport} · ` : ''}
                {h.name}
              </FilterChip>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>
            <span className="font-semibold text-foreground">{board.hotels.length}</span>{' '}
            {t('board.stats.hotels')}
          </span>
          <span>
            <span className="font-semibold text-foreground">{board.totalPnrs}</span>{' '}
            {t('board.stats.bookings')}
          </span>
          <span>
            <span className="font-semibold text-foreground">{board.totalPax}</span>{' '}
            {t('board.stats.guestsPlaced')}
          </span>
          {board.airports.length > 0 ? <span>{board.airports.join(' · ')}</span> : null}
          {board.pendingPnrs > 0 ? (
            <span className="text-amber-600 dark:text-amber-400" data-testid="hotel-board-pending">
              {t('board.awaiting', { count: board.pendingPnrs })}
            </span>
          ) : null}
          {board.truncated ? (
            <span className="text-amber-600 dark:text-amber-400" data-testid="hotel-board-truncated">
              {t('board.truncated')}
            </span>
          ) : null}
        </div>
      </div>

      {/* Hotel cards */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        {visible.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="hotel-board-no-match">
            {t('board.noMatch', { query })}
          </p>
        ) : (
          visible.map((h) => (
            <HotelCard key={h.hotelUrn} hotel={h} extra={renderHotelExtra?.(h)} />
          ))
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      data-testid="hotel-filter-chip"
      aria-pressed={active}
      className={cn(
        'max-w-[14rem] truncate rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}

function HotelCard({ hotel, extra }: { hotel: IHotelOccupancy; extra?: ReactNode }) {
  const { t } = useTranslation('cases');
  return (
    <Card className="overflow-hidden" data-testid="hotel-card">
      {/* Gradient header band with the hotel identity + geo stats */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <Hotel className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-semibold">{hotel.name}</span>
            {hotel.airport ? (
              <Badge variant="info" size="sm">
                {hotel.airport}
              </Badge>
            ) : null}
            <HotelVendorBadge urn={hotel.hotelUrn} />
            {hotel.stars ? <Stars n={hotel.stars} /> : null}
            {hotel.geo ? <MapLink lat={hotel.geo.lat} lon={hotel.geo.lon} name={hotel.name} /> : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {hotel.distanceKm != null ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" aria-hidden />
                {hotel.airport
                  ? t('board.hotel.distanceFrom', {
                      km: hotel.distanceKm.toFixed(1),
                      airport: hotel.airport,
                    })
                  : t('board.hotel.distance', { km: hotel.distanceKm.toFixed(1) })}
              </span>
            ) : null}
            {typeof hotel.perNightUsd === 'number' ? (
              <span className="font-semibold tabular-nums text-foreground">
                {t('board.hotel.perNight', { amount: hotel.perNightUsd })}
              </span>
            ) : hotel.perNightUsd === null ? (
              <Unpriced />
            ) : null}
            {/* The rate was the ONLY money fact on this card — with no night count
                beside it a 1-night and a 4-night stay looked identical. */}
            {hotel.nights != null ? (
              <span className="font-medium text-foreground">
                {t('board.hotel.nights', { count: hotel.nights })}
              </span>
            ) : hotel.nights === null ? (
              <span title={t('board.hotel.nightsMixedTitle')}>{t('board.hotel.nightsMixed')}</span>
            ) : null}
            {hotel.amenities?.length ? (
              <span className="flex flex-wrap items-center gap-1">
                {hotel.amenities.slice(0, 4).map((a) => (
                  <span key={a} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {a}
                  </span>
                ))}
              </span>
            ) : null}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3 text-sm">
          <span
            className="inline-flex items-center gap-1 text-muted-foreground"
            title={
              hotel.roomsBooked === undefined
                ? t('board.booking.roomsUnknown')
                : hotel.estimated
                ? t('board.hotel.roomsEstimatedTitle')
                : t('board.hotel.roomsTitle')
            }
          >
            <BedDouble className="h-4 w-4" aria-hidden />
            <span className="font-semibold text-foreground">
              {hotel.roomsBooked ?? '—'}
              {hotel.estimated ? '*' : ''}
            </span>
          </span>
          <span
            className="inline-flex items-center gap-1 text-muted-foreground"
            title={t('board.hotel.guestsTitle')}
          >
            <Users className="h-4 w-4" aria-hidden />
            <span className="font-semibold text-foreground">{hotel.paxCount}</span>
          </span>
          {/* The airline's price for the hotel's live reservations — «No disponible»
              when the BFF could not price it (no price card), never a 0. */}
          {hotel.spendUsd === null ? (
            <Unpriced className="rounded-md bg-muted px-2 py-0.5 text-xs font-semibold" />
          ) : typeof hotel.spendUsd === 'number' ? (
            <span
              className={
                hotel.estimated || (hotel.spendHeldUsd ?? 0) > 0
                  ? 'rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
              }
              title={
                hotel.estimated
                  ? t('board.hotel.spendEstimatedTitle')
                  : (hotel.spendHeldUsd ?? 0) > 0
                    ? t('board.hotel.spendHeldTitle', {
                        held: (hotel.spendHeldUsd ?? 0).toLocaleString(),
                        booked: (hotel.spendUsd - (hotel.spendHeldUsd ?? 0)).toLocaleString(),
                      })
                    : t('board.hotel.spendBookedTitle')
              }
            >
              ${hotel.spendUsd.toLocaleString()}
              {hotel.estimated
                ? t('board.hotel.estSuffix')
                : (hotel.spendHeldUsd ?? 0) > 0
                  ? t('board.hotel.heldSuffix')
                  : ''}
            </span>
          ) : null}
        </div>
      </div>
      {extra ? (
        <div className="border-b border-border bg-muted/20 px-4 py-1.5" data-testid="hotel-card-extra">
          {extra}
        </div>
      ) : null}
      {/* Booking grid */}
      <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {hotel.pnrs.map((g) => (
          <PnrBooking key={g.subCaseUrn} group={g} />
        ))}
      </div>
    </Card>
  );
}

function MapLink({ lat, lon, name }: { lat: number; lon: number; name: string }) {
  const { t } = useTranslation('cases');
  // OpenStreetMap deep link — opens in a new tab on the operator's click (no embedded
  // tiles / map dependency; geo comes from the booking hotel offer).
  const href = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={t('board.hotel.mapTitle', { name })}
      data-testid="hotel-map-link"
      className="inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline"
    >
      <MapPin className="h-3 w-3" aria-hidden /> {t('board.hotel.map')}
      <ExternalLink className="h-2.5 w-2.5" aria-hidden />
    </a>
  );
}

function Stars({ n }: { n: number }) {
  const { t } = useTranslation('cases');
  return (
    <span
      className="inline-flex items-center"
      aria-label={t('board.hotel.starsLabel', { n })}
      title={t('board.hotel.starsTitle', { n })}
    >
      {Array.from({ length: Math.min(5, Math.max(0, Math.round(n))) }, (_, i) => (
        <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" aria-hidden />
      ))}
    </span>
  );
}

function PnrBooking({ group }: { group: HotelBoardBooking }) {
  const { t } = useTranslation('cases');
  const rooms = group.rooms !== undefined && Number.isSafeInteger(group.rooms) && group.rooms > 0 ? group.rooms : undefined;
  return (
    <div className="rounded-md border border-border bg-muted/30 p-2.5 transition-colors hover:border-primary/40" data-testid="hotel-booking">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-medium">PNR <span>{group.pnrLabel}</span></span>
        <span className="text-[11px] text-muted-foreground">
          {t('units.pax', { n: group.paxCount })}
        </span>
      </div>
      {group.stay ? (
        <StayLine plan={group.stay} compact className="mb-1.5 text-[11px]" />
      ) : null}
      <p className="mb-1.5 text-xs font-medium" data-testid="hotel-booking-rooms">
        {rooms !== undefined ? t('board.booking.rooms', { count: rooms }) : t('board.booking.roomsUnknown')}
      </p>
      {rooms !== undefined ? (
        <div className="mb-2 flex flex-wrap gap-1.5" role="list" aria-label={t('board.booking.roomUnits')}>
          {Array.from({ length: rooms }, (_, index) => (
            <div key={index} role="listitem" aria-label={t('board.booking.roomUnit', { number: index + 1 })}
              className="flex h-10 w-10 items-center justify-center rounded border border-border bg-background"
              data-testid="hotel-room-unit">
              <BedDouble className="h-5 w-5 text-muted-foreground" aria-hidden />
            </div>
          ))}
        </div>
      ) : null}
      <p className="mb-2 text-[11px] text-muted-foreground">{t('board.booking.occupantsUnknown')}</p>
      <p className="mb-1 text-[11px] font-medium text-muted-foreground">{t('board.booking.groupGuests')}</p>
      <div className="flex flex-wrap gap-1" data-testid="hotel-group-guests">
        {group.passengers.map((p) => (
          <span
            key={p.paxUrn}
            title={p.fullName}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary"
          >
            {initials(p)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Two-letter initials from the (masked) pax name, e.g. "J*** D****" → "JD". */
function initials(p: HotelBoardGuest): string {
  const a = (p.firstName ?? '').trim().charAt(0);
  const b = (p.lastName ?? '').trim().charAt(0);
  return (a + b).toUpperCase() || '·';
}
